// THE PARTNER PROFILE on the Partners page (owner, 2026-10-15: "on partner
// page, add the information that is missing about the partner").
//
// Three pieces, kept out of ProjectParticipantsPage so the page only gains
// a few lines (other work edits it at the same time):
//
//   PartnerMissingBanner   "4 partners have missing information" above the
//                          table - counted from each row's `missing`, which
//                          the SERVER computes (back/utils/partnerProfile
//                          .missingInfo), so the page and the reconcile
//                          plan cannot disagree about what is missing.
//   PartnerProfileSummary  under the company name: short code, the trade in
//                          the owner's words, the two contacts, and what is
//                          missing - each gap with a "Fill in" link.
//   PartnerProfileEditor   the dialog that fills it: short code, trade, main
//                          and secondary contact, and an email box for every
//                          person listed by name only.
//
// Saving sends NOBODY anything (back/routes/participantProfile): a person
// given an email here becomes an ordinary roster entry, and inviting them is
// still the page's own "Invite N with no account" button.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Input, Modal, Select, Tag, Tooltip, message } from "antd";
import { updateParticipantProfile } from "api/participants/participantsApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** How many partners have something missing, and which. */
export function missingCount(participants) {
  return (participants || []).filter((r) => r && r.status !== "removed" && (r.missing || []).length).length;
}

/**
 * Every person on this project's partners who has no email yet, one row
 * each: { row, key, name, kind: 'person'|'main'|'secondary' }. A contact who
 * is also a listed person is that person (filling one fills both, on the
 * server), so they appear once.
 */
export function peopleNeedingEmail(participants) {
  const out = [];
  for (const r of participants || []) {
    if (!r || r.status === "removed") continue;
    const m = r.missing || [];
    const people = ((m.find((x) => x.code === "peopleWithoutEmail") || {}).people) || [];
    const names = new Set(people.map((p) => String(p.name || "").trim().toLowerCase()));
    for (const p of people) out.push({ row: r, key: p.key, name: p.name, kind: "person" });
    for (const [code, kind] of [["mainContactEmail", "main"], ["secondaryContactEmail", "secondary"]]) {
      const c = m.find((x) => x.code === code);
      if (c && c.name && !names.has(String(c.name).trim().toLowerCase())) out.push({ row: r, key: `${kind}:${c.name}`, name: c.name, kind });
    }
  }
  return out;
}

export function PartnerMissingBanner({ participants, onShow, onAddEmails }) {
  const n = missingCount(participants);
  const needEmail = peopleNeedingEmail(participants).length;
  if (!n) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={`${n} partner${n === 1 ? " has" : "s have"} missing information`}
      description="Each partner below lists what is missing - a contact, an email, a trade or people. Use Fill in to complete it. Nothing is emailed when you save."
      action={(onShow || (onAddEmails && needEmail)) ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {onAddEmails && needEmail > 0 ? <Button size="small" type="primary" onClick={onAddEmails}>Add missing emails ({needEmail})</Button> : null}
          {onShow ? <Button size="small" onClick={onShow}>Show only these</Button> : null}
        </div>
      ) : null}
    />
  );
}

// EVERY MISSING EMAIL ON THE PROJECT, IN ONE DIALOG (owner, 2026-10-16:
// "make it easy to edit in future and add emails"). The same save as each
// partner's Fill in - one PATCH per partner touched, only the emails typed -
// so a list of addresses that arrives later is one sitting, not eight dialogs.
// Empty boxes are left as they are. Nothing is emailed when you save.
export function PartnerEmailsDialog({ open, participants, projectId, onClose, onSaved }) {
  const rows = useMemo(() => peopleNeedingEmail(participants), [participants]);
  const [emails, setEmails] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setEmails({}); }, [open]);
  const id = (x) => `${x.row._id}|${x.key}`;

  const save = async () => {
    const filled = rows.map((x) => ({ ...x, email: String(emails[id(x)] || "").trim() })).filter((x) => x.email);
    if (!filled.length) { message.info("Type at least one email - empty boxes are left as they are"); return; }
    const bad = filled.find((x) => !EMAIL_RE.test(x.email));
    if (bad) { message.warning(`${bad.name}: that is not an email address`); return; }
    const byRow = new Map();
    for (const x of filled) {
      const k = String(x.row._id);
      if (!byRow.has(k)) byRow.set(k, { row: x.row, body: {} });
      const b = byRow.get(k).body;
      if (x.kind === "person") (b.people = b.people || []).push({ key: x.key, name: x.name, email: x.email });
      else b[x.kind === "main" ? "mainContact" : "secondaryContact"] = { name: x.name, email: x.email };
    }
    setSaving(true);
    const failed = [];
    let done = 0;
    let saved = 0;
    for (const { row, body } of byRow.values()) {
      try {
        await updateParticipantProfile(projectId, row._id, body);
        done += 1;
        saved += (body.people || []).length + (body.mainContact ? 1 : 0) + (body.secondaryContact ? 1 : 0);
      } catch (e) { failed.push(`${row.companyName}: ${e.message || "could not save"}`); }
    }
    setSaving(false);
    if (failed.length) message.error(failed.join("; "));
    if (done) message.success(`Saved ${saved} email${saved === 1 ? "" : "s"} on ${done} partner${done === 1 ? "" : "s"} - nobody was emailed`);
    if (done && onSaved) onSaved();
  };

  return (
    <Modal open={open} title="People without an email" onCancel={onClose} onOk={save} okText="Save" confirmLoading={saving} destroyOnClose width={640}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
        Fill in the ones you have; leave the rest empty. Nobody is emailed when you save - inviting is still the page's own button.
      </div>
      {rows.length === 0 ? <div>Everyone listed has an email.</div> : rows.map((x) => (
        <div key={id(x)} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <span style={{ width: 130, color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={x.row.companyName}>{x.row.companyName}</span>
          <span style={{ width: 170 }}>{x.name}{x.kind !== "person" ? <span style={{ color: "#888", fontSize: 11 }}> ({x.kind === "main" ? "main contact" : "secondary"})</span> : null}</span>
          <Input aria-label={`Email for ${x.name}`} placeholder="Email" value={emails[id(x)] || ""}
            onChange={(e) => { const v = e.target.value; setEmails((m) => ({ ...m, [id(x)]: v })); }} />
        </div>
      ))}
    </Modal>
  );
}

const contactLine = (label, c) => {
  if (!c || !c.name) return null;
  return (
    <div style={{ fontSize: 12, color: "#555" }}>
      {label}: <b>{c.name}</b>
      {c.email ? <span style={{ color: "#888" }}> - {c.email}</span> : <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>Needs an email</Tag>}
    </div>
  );
};

export function PartnerProfileSummary({ row, canEdit, onFill }) {
  if (!row) return null;
  const missing = row.missing || [];
  return (
    <div data-testid="partner-profile" style={{ marginTop: 4 }}>
      {(row.shortCode || row.tradeLabel) && (
        <div style={{ fontSize: 12, color: "#555" }}>
          {row.shortCode ? <Tag style={{ fontSize: 10, lineHeight: "16px" }}>{row.shortCode}</Tag> : null}
          {row.tradeLabel || null}
        </div>
      )}
      {contactLine("Main contact", row.mainContact)}
      {contactLine("Secondary", row.secondaryContact)}
      {missing.length > 0 && (
        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#B45309" }}>Missing:</span>
          {missing.map((m) => (
            <Tooltip key={m.code} title={m.people ? m.people.map((p) => p.name).join(", ") : (m.name || "")}>
              <Tag color="orange" style={{ fontSize: 10, lineHeight: "16px", marginInlineEnd: 0 }}>{m.label}</Tag>
            </Tooltip>
          ))}
          {canEdit && <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => onFill && onFill(row)}>Fill in</Button>}
        </div>
      )}
      {canEdit && !missing.length && (
        <Button type="link" size="small" style={{ padding: 0, height: "auto", fontSize: 12 }} onClick={() => onFill && onFill(row)}>Edit profile</Button>
      )}
    </div>
  );
}

// The people on the row, for "pick the contact from who is already listed".
function rosterPeople(row) {
  const out = [];
  const seen = new Set();
  for (const [list, arr] of [["supervisors", row.supervisors], ["users", row.users]]) {
    for (const p of arr || []) {
      const key = p.email ? `e:${p.email}` : `n:${String(p.name || "").toLowerCase().trim()}`;
      if (!p.name && !p.email) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ value: key, label: `${p.name || p.email}${p.email ? ` - ${p.email}` : " (no email)"}`, name: p.name || p.email, email: p.email || "", list });
    }
  }
  return out;
}

function ContactFields({ label, value, onChange, people, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{label}</div>
      {people.length > 0 && (
        <Select
          size="small"
          allowClear
          placeholder="Pick someone already listed"
          style={{ width: "100%", marginBottom: 6 }}
          options={people}
          value={null}
          onChange={(v) => {
            const p = people.find((x) => x.value === v);
            if (p) onChange({ name: p.name, email: p.email });
          }}
        />
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <Input aria-label={`${label} name`} placeholder="Name" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
        <Input aria-label={`${label} email`} placeholder="Email (if known)" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} />
      </div>
      {hint ? <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{hint}</div> : null}
    </div>
  );
}

export function PartnerProfileEditor({ open, row, projectId, tradeOptions, onClose, onSaved }) {
  const [shortCode, setShortCode] = useState("");
  const [tradeLabel, setTradeLabel] = useState("");
  const [trades, setTrades] = useState([]);
  const [fromLabel, setFromLabel] = useState(false);
  const [main, setMain] = useState({ name: "", email: "" });
  const [second, setSecond] = useState({ name: "", email: "" });
  const [emails, setEmails] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setShortCode(row.shortCode || "");
    setTradeLabel(row.tradeLabel || "");
    setTrades(row.trades || []);
    setFromLabel(false);
    setMain({ name: (row.mainContact && row.mainContact.name) || "", email: (row.mainContact && row.mainContact.email) || "" });
    setSecond({ name: (row.secondaryContact && row.secondaryContact.name) || "", email: (row.secondaryContact && row.secondaryContact.email) || "" });
    setEmails({});
  }, [row]);

  const people = useMemo(() => (row ? rosterPeople(row) : []), [row]);
  const nameOnly = useMemo(() => {
    const m = ((row && row.missing) || []).find((x) => x.code === "peopleWithoutEmail");
    return (m && m.people) || [];
  }, [row]);

  if (!row) return null;

  const save = async () => {
    for (const [label, c] of [["Main contact", main], ["Secondary contact", second]]) {
      if (c.email.trim() && !EMAIL_RE.test(c.email.trim())) { message.warning(`${label}: that is not an email address - leave it empty if you do not have one yet`); return; }
    }
    const filled = nameOnly
      .map((p) => ({ key: p.key, name: p.name, email: String(emails[p.key] || "").trim() }))
      .filter((p) => p.email);
    const bad = filled.find((p) => !EMAIL_RE.test(p.email));
    if (bad) { message.warning(`${bad.name}: that is not an email address`); return; }
    const body = {
      shortCode: shortCode.trim(),
      tradeLabel: tradeLabel.trim(),
      trades,
      addTradesFromLabel: fromLabel,
      mainContact: { name: main.name.trim(), email: main.email.trim() },
      secondaryContact: { name: second.name.trim(), email: second.email.trim() },
      ...(filled.length ? { people: filled } : {}),
    };
    setSaving(true);
    try {
      const saved = await updateParticipantProfile(projectId, row._id, body);
      const left = (saved && saved.missing) || [];
      message.success(left.length ? `Saved - still missing: ${left.map((m) => m.label).join(", ")}` : "Saved - nothing missing for this partner");
      if (onSaved) onSaved(saved);
    } catch (e) {
      message.error(e.message || "Could not save the partner profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`${row.companyName} - partner profile`}
      onCancel={onClose}
      onOk={save}
      okText="Save"
      confirmLoading={saving}
      destroyOnClose
    >
      {(row.missing || []).length > 0 && (
        <Alert
          type="warning"
          style={{ marginBottom: 12 }}
          message={`Missing: ${(row.missing || []).map((m) => m.label).join(", ")}`}
        />
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 120 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Short code</div>
          <Input aria-label="Short code" placeholder="B3D" maxLength={8} value={shortCode} onChange={(e) => setShortCode(e.target.value.toUpperCase())} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Trade, in your words</div>
          <Input aria-label="Trade label" placeholder="Mechanical piping (MP) / Plumbing (PL)" value={tradeLabel} onChange={(e) => setTradeLabel(e.target.value)} />
          <Checkbox checked={fromLabel} onChange={(e) => setFromLabel(e.target.checked)} style={{ marginTop: 4, fontSize: 12 }}>
            Also add the trades this names
          </Checkbox>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>Trades</div>
        <Select aria-label="Trades" mode="tags" style={{ width: "100%" }} value={trades} onChange={setTrades} options={tradeOptions || []} placeholder="Disciplines this partner covers" />
      </div>
      <ContactFields label="Main contact" value={main} onChange={setMain} people={people}
        hint="Listed as a supervisor of this partner." />
      <ContactFields label="Secondary contact" value={second} onChange={setSecond} people={people}
        hint="Listed as a user of this partner." />
      {nameOnly.length > 0 && (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>People without an email</div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
            They show in every dropdown under {row.companyName}, greyed out until they have an email.
          </div>
          {nameOnly.map((p) => (
            <div key={p.key} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
              <span style={{ width: 160 }}>{p.name}</span>
              <Input
                aria-label={`Email for ${p.name}`}
                placeholder="Email"
                value={emails[p.key] || ""}
                onChange={(e) => setEmails((m) => ({ ...m, [p.key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
